import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveIcon from "@mui/icons-material/Save";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FONT = "'Inter', 'Roboto', sans-serif";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function buildYearList() {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

export default function LeaveCalendar() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [markedDates, setMarkedDates] = useState(new Set()); // dates toggled by staff
  const [savedDates, setSavedDates] = useState(new Set()); // dates already approved/saved
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadLeaveDates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(
        `staff/leave-calendar?month=${viewMonth + 1}&year=${viewYear}`
      );
      const data = res?.data;
      if (Array.isArray(data) && data.length > 0) {
        setSavedDates(new Set(data.map((d) => String(d).slice(0, 10))));
      } else if (data?.dates) {
        setSavedDates(new Set(data.dates.map((d) => String(d).slice(0, 10))));
      } else {
        setSavedDates(new Set());
      }
    } catch {
      setSavedDates(new Set());
    } finally {
      setLoading(false);
    }
  }, [viewMonth, viewYear]);

  useEffect(() => {
    setMarkedDates(new Set());
    loadLeaveDates();
  }, [loadLeaveDates]);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const toISO = (day) =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const handleDayClick = (day) => {
    if (!day) return;
    const iso = toISO(day);
    const cellDate = new Date(iso);
    cellDate.setHours(0, 0, 0, 0);

    // Only allow future dates (not today or past)
    if (cellDate <= today) return;

    // Cannot un-mark already saved dates (admin-applied)
    if (savedDates.has(iso)) return;

    const dow = cellDate.getDay();
    if (dow === 0 || dow === 6) return; // no weekends

    setMarkedDates((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });
  };

  const handleSave = async () => {
    if (markedDates.size === 0) {
      setToast({ open: true, severity: "info", message: "No new dates selected to save." });
      return;
    }
    setSaving(true);
    try {
      await postData("staff/leave-calendar", {
        dates: Array.from(markedDates),
        month: viewMonth + 1,
        year: viewYear,
      });
      setToast({ open: true, severity: "success", message: `${markedDates.size} leave date(s) saved successfully.` });
      // Move marked dates to saved
      setSavedDates((prev) => new Set([...prev, ...markedDates]));
      setMarkedDates(new Set());
    } catch {
      setToast({ open: true, severity: "error", message: "Failed to save leave dates. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const getDayStyle = (day) => {
    if (!day) return {};
    const iso = toISO(day);
    const cellDate = new Date(iso);
    cellDate.setHours(0, 0, 0, 0);
    const dow = cellDate.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isPast = cellDate <= today;
    const isToday =
      day === today.getDate() &&
      viewMonth === today.getMonth() &&
      viewYear === today.getFullYear();
    const isSaved = savedDates.has(iso);
    const isMarked = markedDates.has(iso);

    if (isToday) {
      return {
        backgroundColor: "#244C77",
        color: "#fff",
        fontWeight: "bold",
        cursor: "default",
      };
    }
    if (isSaved) {
      return {
        backgroundColor: "#ff9800",
        color: "#fff",
        fontWeight: "bold",
        cursor: "default",
      };
    }
    if (isMarked) {
      return {
        backgroundColor: "#ffe0b2",
        color: "#e65100",
        border: "2px solid #ff9800",
        fontWeight: "bold",
        cursor: "pointer",
      };
    }
    if (isWeekend) {
      return {
        backgroundColor: "#F3F4F6",
        color: "#C4C4C4",
        cursor: "default",
      };
    }
    if (isPast) {
      return {
        backgroundColor: "transparent",
        color: "#BDBDBD",
        cursor: "default",
      };
    }
    return {
      backgroundColor: "transparent",
      color: "#1a1a2e",
      cursor: "pointer",
    };
  };

  return (
    <Box
      sx={{
        padding: "1.5rem",
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily: FONT,
      }}
    >
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 0.5 }}>
        Pages / Leave Calendar
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e", fontFamily: FONT }}>
            Leave Calendar
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#8F95B2" }}>
            Click a future weekday to mark / unmark planned leave
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || markedDates.size === 0}
          sx={{
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            color: "#fff",
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 600,
            px: 3,
            "&:hover": { background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)" },
            "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
          }}
        >
          {saving ? "Saving…" : `Save ${markedDates.size > 0 ? `(${markedDates.size})` : ""}`}
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 14px rgba(80,56,237,0.10)",
          maxWidth: 780,
          mx: "auto",
        }}
      >
        {/* Gradient header with navigation */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={prevMonth}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              p: 0,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.15)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
            }}
          >
            <ChevronLeftIcon />
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" variant="standard">
              <Select
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                disableUnderline
                sx={{ color: "#fff", fontWeight: "bold", fontSize: "18px", fontFamily: FONT }}
              >
                {MONTHS.map((m, i) => (
                  <MenuItem key={i} value={i} sx={{ fontSize: "14px" }}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" variant="standard">
              <Select
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                disableUnderline
                sx={{ color: "#fff", fontWeight: "bold", fontSize: "18px", fontFamily: FONT }}
              >
                {buildYearList().map((y) => (
                  <MenuItem key={y} value={y} sx={{ fontSize: "14px" }}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            onClick={nextMonth}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              p: 0,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.15)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
            }}
          >
            <ChevronRightIcon />
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#244C77" }} />
            </Box>
          ) : (
            <>
              {/* Week day headers */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: "4px",
                  mb: 1,
                }}
              >
                {WEEK_DAYS.map((d) => (
                  <Box
                    key={d}
                    sx={{
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: d === "Sun" || d === "Sat" ? "#BDBDBD" : "#6B7280",
                      py: "6px",
                      fontFamily: FONT,
                    }}
                  >
                    {d}
                  </Box>
                ))}
              </Box>

              {/* Day cells */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: "4px",
                }}
              >
                {cells.map((day, idx) => {
                  const style = getDayStyle(day);
                  return (
                    <Box
                      key={idx}
                      onClick={() => handleDayClick(day)}
                      sx={{
                        textAlign: "center",
                        py: "10px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        minHeight: "42px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s",
                        userSelect: "none",
                        "&:hover":
                          day &&
                          !savedDates.has(toISO(day)) &&
                          new Date(toISO(day)) > today &&
                          new Date(toISO(day)).getDay() !== 0 &&
                          new Date(toISO(day)).getDay() !== 6
                            ? { backgroundColor: "#EAF1F8", color: "#244C77" }
                            : {},
                        ...style,
                      }}
                    >
                      {day || ""}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Legend */}
          <Box sx={{ display: "flex", gap: 2.5, mt: 3, flexWrap: "wrap", pt: 2, borderTop: "1px solid #F3F4F6" }}>
            {[
              { color: "#244C77", label: "Today" },
              { color: "#ff9800", label: "Saved Leave" },
              { color: "#ffe0b2", label: "Selected (unsaved)", border: "2px solid #ff9800" },
              { color: "#F3F4F6", label: "Weekend", textColor: "#C4C4C4" },
            ].map(({ color, label, border, textColor }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "4px",
                    backgroundColor: color,
                    border: border || "none",
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: "12px", color: textColor || "#6B7280", fontFamily: FONT }}>
                  {label}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <EventBusyIcon sx={{ fontSize: "14px", color: "#BDBDBD" }} />
              <Typography sx={{ fontSize: "12px", color: "#6B7280", fontFamily: FONT }}>
                Past / Not selectable
              </Typography>
            </Box>
          </Box>

          {/* Selected dates summary */}
          {markedDates.size > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#FFF8EE",
                borderRadius: "10px",
                border: "1px solid #FDDEA0",
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#92400E", mb: 0.5 }}>
                {markedDates.size} date(s) selected:
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#92400E" }}>
                {Array.from(markedDates)
                  .sort()
                  .map((d) =>
                    new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
                  )
                  .join(" · ")}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
